import React, { useState, useEffect } from "react";
import { Container, Row, Col, Modal, Spinner, Image } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {  FaImages } from "react-icons/fa";
import CommonBanner from "../Components/Common";
import gallery_banner from "../Assests/gallery_banner.png";
import BASE_URL from "../Components/constant";
import axios from "axios";
import "./Gallery.css";

const Galary = () => {
  const { t } = useTranslation();
  const [galleryData, setGalleryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentYearGroup, setCurrentYearGroup] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);

  // Fetch gallery data from API
  useEffect(() => {
    const fetchGallery = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BASE_URL}/api/v1/gallery`);
        if (response.data?.success && response.data?.data) {
          // Group images by year
          const grouped = response.data.data.reduce((acc, item) => {
            if (!acc[item.year]) {
              acc[item.year] = [];
            }
            acc[item.year].push(item);
            return acc;
          }, {});
          
          // Sort years in descending order (latest first)
          const sortedYears = Object.keys(grouped).sort((a, b) => b.localeCompare(a));
          const sortedGrouped = {};
          sortedYears.forEach(year => {
            sortedGrouped[year] = grouped[year];
          });
          
          setGalleryData(sortedGrouped);
        }
      } catch (error) {
        console.error("Error fetching gallery:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  // Handle image click to open lightbox
  const handleImageClick = (image, year, index) => {
    setSelectedImage(image);
    setCurrentYearGroup(year);
    setCurrentIndex(index);
    setShowModal(true);
  };

  // Navigation functions for lightbox
  const nextImage = () => {
    const currentGroupImages = galleryData[currentYearGroup];
    if (currentGroupImages && currentIndex < currentGroupImages.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedImage(currentGroupImages[currentIndex + 1]);
    }
  };

  const prevImage = () => {
    const currentGroupImages = galleryData[currentYearGroup];
    if (currentGroupImages && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setSelectedImage(currentGroupImages[currentIndex - 1]);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showModal) return;
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "Escape") setShowModal(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showModal, currentIndex, currentYearGroup, galleryData]);

  // Skeleton loader component
  const SkeletonLoader = () => (
    <div className="skeleton-wrapper">
      {[...Array(8)].map((_, idx) => (
        <div key={idx} className="skeleton-card">
          <div className="skeleton-image shimmer"></div>
          <div className="skeleton-overlay"></div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <CommonBanner image={gallery_banner} />
      
      <Container  className="gallery-page">
        {/* Header Section */}
        <div className="gallery-header">
          <h1 className="gallery-title">
            {"Gallery"}
          </h1>
          <p className="gallery-subtitle">
           Capturing moments of impact and transformation
          </p>
        </div>
        {loading ? (
          <SkeletonLoader />
        ) : (
          Object.entries(galleryData).map(([year, images]) => (
            <div key={year} className="gallery-year-section">
              <div className="year-header">
                <div className="year-badge-media">
                  <FaImages className="year-icon" />
                  <h2 className="year-title">{year}</h2>
                </div>
                <div className="year-divider"></div>
              </div>
              
              <Row className="gallery-grid">
                {images.map((image, idx) => (
                  <Col 
                    key={image.id || idx} 
                    lg={3} md={4} sm={6} xs={12} 
                    className="gallery-col"
                  >
                    <div 
                      className="gallery-card"
                      onClick={() => handleImageClick(image, year, idx)}
                    >
                      <div className="image-wrapper">
                        <img
                          src={image.mobile_image}
                          alt={`Gallery ${year} ${idx + 1}`}
                          className="gallery-image"
                          loading="lazy"
                        />
                        <div className="image-overlay">
                          <div className="zoom-icon">🔍</div>
                        </div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          ))
        )}

 
        // In your Gallery component, replace the Modal section with this:

<Modal 
  show={showModal} 
  onHide={() => setShowModal(false)}
  size="lg"
  centered
  className="gallery-lightbox"
  fullscreen={true}  // This makes it fullscreen on mobile
  scrollable={false}
>
  <Modal.Body className="lightbox-body" style={{ padding: 0, position: 'relative' }}>
    <button 
      className="close-btn" 
      onClick={() => setShowModal(false)}
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 1060,
        background: 'rgba(0,0,0,0.7)',
        border: 'none',
        color: 'white',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      ✕
    </button>
    
    {selectedImage && (
      <div style={{ 
        width: '100%', 
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'black',
        position: 'relative'
      }}>
        <button 
          onClick={prevImage}
          style={{
            position: 'fixed',
            left: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'rgba(0,0,0,0.5)',
            border: 'none',
            color: 'white',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            zIndex: 1060
          }}
        >
          ◀
        </button>
        
        <img
          src={selectedImage.desktop_image || selectedImage.mobile_image}
          alt="Preview"
          style={{
            maxWidth: '100%',
            maxHeight: '100vh',
            objectFit: 'contain'
          }}
        />
        
        <button 
          onClick={nextImage}
          style={{
            position: 'fixed',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'rgba(0,0,0,0.5)',
            border: 'none',
            color: 'white',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            zIndex: 1060
          }}
        >
          ▶
        </button>
        
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.7)',
          color: 'white',
          padding: '5px 15px',
          borderRadius: '20px',
          fontSize: '12px',
          zIndex: 1060
        }}>
          {currentIndex + 1} / {galleryData[currentYearGroup]?.length || 0}
        </div>
      </div>
    )}
  </Modal.Body>
</Modal>
      </Container>
    </>
  );
};

export default Galary;